// src/mocks/handlers/funding/documentsHandler.ts

import { rest } from 'msw';
import documents from '@/mocks/data/fundingDocumentsData.json';
import { API_ENDPOINTS } from '@/services/api/endpoints';
import { VITE_MSW_BASE_URL } from '@/config';
import {
  documentsResponse,
  IDocument,
  TDocumentType,
} from '@/domains/funding/documents.types';
import { isAccessTokenExpired } from '@/mocks/lib/authTokenUtils';

const filterDocuments = (
  results: documentsResponse['results'],
  docTypes: TDocumentType[] | null,
  currencies: string[] | null,
  version: string | null,
): documentsResponse['results'] => {
  return results
    .map((yearGroup) => ({
      ...yearGroup,
      documents: yearGroup.documents.filter((document: IDocument) => {
        if (docTypes?.length && !docTypes.includes(document.doc_type)) {
          return false;
        }
        if (
          currencies?.length &&
          (!document.currency || !currencies.includes(document.currency))
        ) {
          return false;
        }
        if (version && document.version !== version) {
          return false;
        }
        return true;
      }),
    }))
    .filter((yearGroup) => yearGroup.documents.length > 0);
};

export const fundingDocumentsHandler = rest.get<documentsResponse>(
  `${VITE_MSW_BASE_URL}${API_ENDPOINTS.FUNDING_DOCUMENTS}`,
  (req, res, ctx) => {
    const token = req.headers.get('Authorization');
    if (!token) {
      return res(ctx.status(500), ctx.json({ detail: 'Something went wrong' }));
    }

    if (isAccessTokenExpired()) {
      return res(ctx.status(401), ctx.json({ error: 'Access token expired' }));
    }

    const url = new URL(req.url);
    const docTypes = url.searchParams.getAll('doc_type') as TDocumentType[];
    const currencies = url.searchParams.getAll('currency');
    const version = url.searchParams.get('version');

    const filteredResults = filterDocuments(
      documents.results as documentsResponse['results'],
      docTypes.length > 0 ? docTypes : null,
      currencies.length > 0 ? currencies : null,
      version,
    );

    return res(ctx.status(200), ctx.json({ results: filteredResults }));
  },
);
